import {Box, Button, Dialog, DialogActions, DialogContent, useTheme} from "@mui/material";
import {useContext, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useRecoilState} from "recoil";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import BootstrapDialogTitle from "../../../common/components/BootstrapDialogTitle";
import CustomStepper from "../../../common/components/CustomStepper";
import {initialUpsellingRateCreation, upsellingRateCreationAtom} from "../../../state/upsellingRatesRecoil";
import UpsellingRateAssociation from "./ASSOCIATION/UpsellingRateAssociation";
import UpsellingRateBaseDefinition from "./DEFINITION/UpsellingRateBaseDefinition";
import UpsellingRateMatrixDefinition from "./MATRIX/UpsellingRateMatrixDefinition";
import {BASE_DEFINITION_FORM_STEPS} from "../../../constants";
import * as yup from "yup";
import {FormProvider, useForm} from "react-hook-form";
import {FormUpsellingRateAssociationModel, FormUpsellingRateModel} from "../../../api/types/upsellingRate";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";

type UpsellingRateCreationFormProps = {
    isOpen: boolean;
    onDone: () => void;
    onClose: () => void;
    onStepChange?: (index: number) => void;
    refresh: Function;
};

const UpsellingRateFormContainer = ({
                                        isOpen,
                                        onClose,
                                        onStepChange,
                                        onDone,
                                        refresh
                                    }: UpsellingRateCreationFormProps) => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // use Theme
    const theme = useTheme();

    // Use Recoil
    const [upsellingRateCreation, setUpsellingRateCreation] = useRecoilState(upsellingRateCreationAtom);
    const {readOnly, completed, step, definitionData} = upsellingRateCreation;

    // Use State
    const [initialValues] = useState<FormUpsellingRateModel>({
        commercialCategory: null,
        commercialSegmentId: null,
        name: "",
        from: "",
        to: "",
        description: "",
        dealer: null,
        legalEntity: null
    });

    const [initialValuesAssociation] = useState<FormUpsellingRateAssociationModel>({
        reservedCarGroups: [],
        updatedCarGroups: []
    });

    const submitRef = useRef<HTMLButtonElement>(null);

    // Use Context
    const {dispatchGlobalData} = useContext(GlobalContext);

    const currentStep = () => {
        switch (step) {
            case BASE_DEFINITION_FORM_STEPS.DEFINITION:
                return <UpsellingRateBaseDefinition submitRef={submitRef} refresh={refresh}/>;
            case BASE_DEFINITION_FORM_STEPS.ASSOCIATION:
                return <FormProvider {...formAssociation}><UpsellingRateAssociation submitRef={submitRef}
                                                                                    refresh={refresh}/></FormProvider>;
            case BASE_DEFINITION_FORM_STEPS.MATRIX:
                return <UpsellingRateMatrixDefinition submitRef={submitRef}/>;
            case BASE_DEFINITION_FORM_STEPS.DONE:
                onDone();
                setUpsellingRateCreation(initialUpsellingRateCreation);
                return;
            default:
                return null;
        }
    };

    const validationSchema = yup
        .object()
        .shape({
            name: yup.string().required(t("translation:validation.fieldMandatory")).min(2),
            commercialCategory: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string()
                })
                .required(t("translation:validation.fieldMandatory"))
                .nullable(true),
            commercialSegmentId: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string()
                })
                .required(t("translation:validation.fieldMandatory"))
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            description: yup.string().required(t("translation:validation.fieldMandatory")),
            from: yup
                .string()
                .required(t("translation:validation.fieldMandatory"))
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            to: yup
                .string()
                .required(t("translation:validation.fieldMandatory"))
                .nullable()
                .required(t("translation:validation.fieldMandatory"))
        })
        .defined();

    // Use Form Context
    const form = useForm<FormUpsellingRateModel>({
        defaultValues: definitionData || initialValues,
        resolver: yupResolver(
            validationSchema,
            {},
            {
                rawValues: true
            }
        ),
        shouldUnregister: true
    });

    const validationSchemaAssociation = yup
        .object()
        .shape({
            updatedCarGroups: yup
                .array()
                .of(
                    yup.string()
                )
                .required(t("translation:validation.fieldMandatory"))
                .min(1, t("translation:validation.fieldMandatory"))
                .nullable(true),
            reservedCarGroups: yup
                .array()
                .of(
                    yup.string()
                )
                .required(t("translation:validation.fieldMandatory"))
                .min(1, t("translation:validation.fieldMandatory"))
                .nullable(true)
        })
        .defined();

    // Use Form Context
    const formAssociation = useForm<FormUpsellingRateAssociationModel>({
        defaultValues: useMemo(() => initialValuesAssociation, [initialValuesAssociation]),
        resolver: yupResolver(
            validationSchemaAssociation,
            {},
            {
                rawValues: true
            }
        ),
        shouldUnregister: false
    });

    const onSubmit = () => {
        submitRef?.current?.click();
    };

    const handleOnClose = () => {
        onClose();
        form.reset(initialValues)
        formAssociation.reset(initialValuesAssociation)
    };

    const saveAndQuit = async () => {
        if (step === 0) {
            const commercialCategory = await form.trigger("commercialCategory")
            const commercialSegmentId = await form.trigger("commercialSegmentId")
            const name = await form.trigger("name")
            const from = await form.trigger("from")
            const to = await form.trigger("to")
            const description = await form.trigger("description")
            if (commercialCategory && commercialSegmentId && name && from && to && description) {
                onSubmit();
                handleOnClose();
            }
        } else if (step === 1) {
            const updatedCarGroups = await formAssociation.trigger("updatedCarGroups")
            const reservedCarGroups = await formAssociation.trigger("reservedCarGroups")
            if (updatedCarGroups && reservedCarGroups) {
                onSubmit();
                handleOnClose();
            }
        }
    };

    const handleChangeStep = (clickedStep: number) => {
        if (clickedStep <= step || completed.slice(step, clickedStep).every((value) => value === true)) {
            onStepChange && onStepChange(clickedStep);
            setUpsellingRateCreation({...upsellingRateCreation, step: clickedStep});
        } else {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("pricing:steps.warningNextStep"),
                        type: snackbarTypes.warning,
                        show: true,
                        autoClose: true
                    }
                }
            });
        }
    };

    return (
        <Dialog open={isOpen} maxWidth={"xl"} fullWidth>
            <BootstrapDialogTitle id="customized-dialog-title" onClose={handleOnClose}>
                <Box sx={{display: "flex", alignItems: "center", width: "100%"}}>
                    <CustomStepper
                        activeStep={step}
                        completed={completed}
                        steps={[
                            t("pricing:steps.definition"),
                            t("pricing:steps.association"),
                            t("pricing:steps.matrix")
                        ]}
                        handleStep={handleChangeStep}
                    />
                </Box>
            </BootstrapDialogTitle>
            <DialogContent style={{color: theme.palette.primary.main}}>
                <FormProvider {...form}>
                    {currentStep()}
                </FormProvider>
            </DialogContent>
            {!readOnly ? (
                <DialogActions style={{backgroundColor: theme.palette.neutral.bright, padding: "1rem"}}>
                    <Button variant="outlined" color={"accent"} onClick={handleOnClose}>
                        {t("pricing:actions.cancel")}
                    </Button>
                    {step === BASE_DEFINITION_FORM_STEPS.MATRIX || (
                        <Button variant="outlined" color={"accent"} onClick={saveAndQuit}>
                            {t("pricing:actions.saveAndExit")}
                        </Button>
                    )}
                    <Button
                        sx={{marginLeft: "16px"}}
                        color={"accent"}
                        onClick={onSubmit}
                        onSubmit={onSubmit}
                    >
                        {step === BASE_DEFINITION_FORM_STEPS.MATRIX
                            ? t("pricing:actions.saveAndExit")
                            : t("pricing:actions.saveAndProceed")}
                    </Button>
                </DialogActions>
            ) : (
                <div style={{padding: "1rem"}}/>
            )}
        </Dialog>
    );
};

export default UpsellingRateFormContainer;
